/**
 * Lista artykułów z bloga sianko.pl promowanych w aplikacji rodzica.
 *
 * Maskotka rodzica (Mama/Tata Sianko) raz na ~7 dni przypomina o
 * artykule. Klik bubble → otwarcie w nowej karcie + zapis
 * `prefs.seenMoments["blog:{slug}"]`.
 *
 * Rotacja: `getNextBlogPost` wybiera **kolejny** artykuł nie widziany
 * lub obejrzany dawniej niż 7 dni — żeby z czasem przewinąć całą
 * bibliotekę, a nie zapętlać tego samego tekstu.
 *
 * Dodawanie nowych: dorzuć obiekt na początku tablicy (ostatnio
 * opublikowany first). Algorytm rotacji szanuje ten porządek.
 *
 * Rozszerzenie do Firestore w przyszłości: zamień hardcoded array na
 * `useLatestBlogPost` listener kolekcji `/blogPosts`. Schema bez zmian.
 */

export type BlogPost = {
  slug: string;
  title: string;
  url: string;
  /** ISO yyyy-mm-dd. */
  publishedAt: string;
  /**
   * Zaczepka do dymka maskotki — pytanie / teaser („Znasz test pianki?").
   * Dymek dokłada CTA „Przeczytaj na blogu →" w UI, więc hook to sama
   * zaczepka. Bez „świeży wpis" — artykuły rotują, nie są nowe. Max ~40 zn.
   */
  hook: string;
};

export const BLOG_POSTS: readonly BlogPost[] = [
  {
    slug: "zabieranie-kieszonkowego-za-kare",
    title:
      "Kary finansowe w domu — dlaczego zabieranie kieszonkowego za karę to błąd",
    url: "https://sianko.pl/blog/zabieranie-kieszonkowego-za-kare",
    publishedAt: "2026-06-11",
    hook: "Zabierasz kieszonkowe za karę?",
  },
  {
    slug: "oszczedzanie-zasada-50-50",
    title:
      "Zasada 50/50 — jak nauczyć dziecko oszczędzać na rzeczy, które wydają się za drogie",
    url: "https://sianko.pl/blog/oszczedzanie-zasada-50-50",
    publishedAt: "2026-06-11",
    hook: "Cel za drogi? Dołóż drugą połowę.",
  },
  {
    slug: "deepfake-rodzinne-haslo",
    title: "Deepfake na wnuczka — rodzinne hasło bezpieczeństwa w erze AI",
    url: "https://sianko.pl/blog/deepfake-rodzinne-haslo",
    publishedAt: "2026-05-09",
    hook: "AI podrobi Twój głos. Macie hasło?",
  },
  {
    slug: "magia-karty-platniczej",
    title: "Magia karty płatniczej — jak uczyć dziecko, że BLIK to nie czary",
    url: "https://sianko.pl/blog/magia-karty-platniczej",
    publishedAt: "2026-05-09",
    hook: "Dla dziecka BLIK to czary?",
  },
  {
    slug: "test-pianki-trzy-sloiki",
    title:
      "Test pianki, trzy słoiki, procent składany — trzy lekcje oszczędzania",
    url: "https://sianko.pl/blog/test-pianki-trzy-sloiki",
    publishedAt: "2026-05-09",
    hook: "Znasz test pianki?",
  },
  {
    slug: "rodzic-jako-pierwsza-ksiazka",
    title: "Rodzic jako pierwsza książka o finansach",
    url: "https://sianko.pl/blog/rodzic-jako-pierwsza-ksiazka",
    publishedAt: "2026-05-09",
    hook: "Finansów uczysz przykładem, nie radą.",
  },
  {
    slug: "placenie-za-oceny",
    title:
      "Pięćdziesiąt złotych za piątkę — dlaczego płacenie za oceny psuje motywację",
    url: "https://sianko.pl/blog/placenie-za-oceny",
    publishedAt: "2026-05-09",
    hook: "50 zł za piątkę? To psuje motywację.",
  },
  {
    slug: "nudzi-sie-i-dobrze",
    title: "Nudzi się — i dobrze. O byciu offline w 2026",
    url: "https://sianko.pl/blog/nudzi-sie-i-dobrze",
    publishedAt: "2026-05-09",
    hook: "Dziecko się nudzi? I bardzo dobrze.",
  },
  {
    slug: "czy-placic-za-obowiazki",
    title: "Czy płacić dzieciom za obowiązki domowe?",
    url: "https://sianko.pl/blog/czy-placic-za-obowiazki",
    publishedAt: "2026-05-09",
    hook: "Za które obowiązki płacić, a za które nie?",
  },
  {
    slug: "pierwsze-kieszonkowe",
    title: "Pierwsze kieszonkowe — od ilu lat i ile?",
    url: "https://sianko.pl/blog/pierwsze-kieszonkowe",
    publishedAt: "2026-05-09",
    hook: "Pierwsze kieszonkowe — ile i od kiedy?",
  },
] as const;

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Wybiera kolejny artykuł do pokazania użytkownikowi:
 *  1. Najnowszy (po publish order) nieobejrzany lub widziany dawniej
 *     niż 7 dni → pokazujemy jego.
 *  2. Wszystkie świeższe niż 7 dni → wybieramy najdawniej widzianego
 *     (cykl po bibliotece, nie zapętlanie tego samego).
 *
 * `seenMoments` = `users/{uid}.prefs.seenMoments` — mapa
 * `"blog:{slug}" → ISO date string` aktualizowana po kliknięciu bubble.
 */
export function getNextBlogPost(
  seenMoments?: Record<string, string>,
): BlogPost | null {
  if (BLOG_POSTS.length === 0) return null;
  const now = Date.now();

  const fresh = BLOG_POSTS.find((post) => {
    const seenIso = seenMoments?.[`blog:${post.slug}`];
    if (!seenIso) return true;
    return now - new Date(seenIso).getTime() > SEVEN_DAYS_MS;
  });
  if (fresh) return fresh;

  // Wszystkie obejrzane w ostatnich 7 dniach — najdawniej widziany pierwszy.
  return (
    [...BLOG_POSTS].sort((a, b) => {
      const ta = new Date(seenMoments?.[`blog:${a.slug}`] ?? 0).getTime();
      const tb = new Date(seenMoments?.[`blog:${b.slug}`] ?? 0).getTime();
      return ta - tb;
    })[0] ?? null
  );
}
